import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, CheckCircle2 } from 'lucide-react'

/**
 * ProductStrainMatchModal
 *
 * Allows users to match imported product strains to existing strains in the database.
 * This modal only handles strain matching - types are already resolved from ProductTypeMatchModal.
 * Products will be created/matched based on strain + type combinations automatically.
 *
 * @param {boolean} open - Whether modal is visible
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onConfirm - Callback with strain mappings when confirmed
 * @param {Array} uniqueStrains - Array of unique strain strings from import
 * @param {Array} existingProducts - Array of existing products from database
 * @param {Object} initialMappings - Pre-existing mappings to restore (for session recovery)
 */
export default function ProductStrainMatchModal({
  open,
  onClose,
  onConfirm,
  uniqueStrains = [],
  existingProducts = [],
  initialMappings = {}
}) {
  // State: { "Grey Oyster": { action: "match", strain: "Grey Oyster" } } or { action: "create", strain: "Grey Oyster" }
  const [strainMappings, setStrainMappings] = useState({})

  // Get unique strains from existing products for matching (memoized to prevent infinite loops)
  const existingStrains = useMemo(
    () => [...new Set(existingProducts.map(p => p.strain).filter(Boolean))],
    [existingProducts]
  )

  // Initialize mappings when modal opens
  useEffect(() => {
    if (open && uniqueStrains.length > 0) {
      const newMappings = {}

      uniqueStrains.forEach(csvStrain => {
        // First, check if we have a pre-existing mapping (from session recovery)
        if (initialMappings[csvStrain]) {
          newMappings[csvStrain] = initialMappings[csvStrain]
        } else {
          // Try to find exact match (case-insensitive)
          const exactMatch = existingStrains.find(
            dbStrain => dbStrain.toLowerCase().trim() === csvStrain.toLowerCase().trim()
          )

          if (exactMatch) {
            newMappings[csvStrain] = {
              action: 'match',
              strain: exactMatch
            }
          } else {
            // Default to create new
            newMappings[csvStrain] = {
              action: 'create',
              strain: csvStrain
            }
          }
        }
      })

      setStrainMappings(newMappings)
    }
  }, [open, uniqueStrains, existingStrains, initialMappings])

  const handleMatchChange = (csvStrain, value) => {
    if (value === 'create_new') {
      setStrainMappings(prev => ({
        ...prev,
        [csvStrain]: {
          action: 'create',
          strain: csvStrain
        }
      }))
    } else {
      // Matching to existing strain
      setStrainMappings(prev => ({
        ...prev,
        [csvStrain]: {
          action: 'match',
          strain: value
        }
      }))
    }
  }

  const handleConfirm = () => {
    // Validate: all strains must be mapped
    const allMapped = uniqueStrains.every(strain => strainMappings[strain])
    if (!allMapped) {
      alert('Please map all strains before proceeding')
      return
    }

    onConfirm(strainMappings)
  }

  const allStrainsMapped = uniqueStrains.length > 0 && uniqueStrains.every(strain => strainMappings[strain])

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="odin-modal-large max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Match Product Strains</DialogTitle>
          <DialogDescription>
            Map imported product strains (e.g., Grey Oyster, Lion's Mane) to existing strains in your database, or create new ones.
            Products will be created automatically by combining these strains with the types you've already mapped.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-4">
          {uniqueStrains.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No product strains found in import data
            </div>
          ) : (
            uniqueStrains.map(csvStrain => {
              const mapping = strainMappings[csvStrain]
              const isMatched = mapping?.action === 'match'
              const isCreating = mapping?.action === 'create'

              return (
                <div key={csvStrain} className="border rounded-lg p-4 space-y-3 bg-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{csvStrain}</span>
                      {isMatched && mapping.strain !== csvStrain && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                          → {mapping.strain}
                        </Badge>
                      )}
                      {isMatched && mapping.strain === csvStrain && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Matched
                        </Badge>
                      )}
                      {isCreating && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                          New Strain
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor={`strain-select-${csvStrain}`}>Action</Label>
                    <Select
                      value={mapping?.action === 'match' ? mapping.strain : 'create_new'}
                      onValueChange={(value) => handleMatchChange(csvStrain, value)}
                    >
                      <SelectTrigger id={`strain-select-${csvStrain}`}>
                        <SelectValue placeholder="Select action" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="create_new">Create New Strain: {csvStrain}</SelectItem>
                        {existingStrains.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                              Match to Existing Strain
                            </div>
                            {existingStrains.map(strain => (
                              <SelectItem key={strain} value={strain}>
                                {strain}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )
            })
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 text-sm">
              {allStrainsMapped ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">All strains mapped</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-600">
                    {uniqueStrains.filter(s => !strainMappings[s]).length} strain(s) remaining
                  </span>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={!allStrainsMapped}>
                Continue to Preview
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
